import { defineModule, definePermission } from '@shared/contract'

// Finanzen: die kaufmännische Seite der Warenwirtschaft – Ausgangsrechnungen und Rechnungskorrekturen, offene Posten
// von Kunden und Lieferanten, Zahlungen, Mahnwesen, Periodenabschluss und die Übergabe an die Steuerkanzlei.
// Keine Finanzbuchhaltung: Das Buchungsjournal ist eine Leseansicht, abgeleitet aus den Belegen und Zahlungen.
//
// Rechte (db/seeds/demo/roles.ts, Abschnitt „Finanzen“):
//   * Rechnungen, offene Posten und Zahlungen sind je Standort vergebbar – die Filialleitung sieht, was an ihrem
//     Standort berechnet und kassiert wurde. Der Fahrer hält `payment.record` nur an seinem Fahrzeug.
//   * Fahrerkasse: `driverCash.settle` (abgeben, am Fahrzeug) und `driverCash.approve` (zweite Person, bucht).
//   * Zahlbare Eingangsrechnungen, Mahnwesen, Abschluss und Export gelten für den ganzen Betrieb.
//   * `period.reopen` ist das starke Recht: Eine abgeschlossene Periode öffnet nur, wer es hält – mit Begründung.
//   * Per E-Mail versenden ist ein eigenes Recht: `invoice.send` je Standort, `dunning.send` für den ganzen Betrieb
//     (docs/anmeldung/SPEZIFIKATION.md §8.2).
// E-Rechnung: Die XML erzeugt das Modul `einvoice` beim Festschreiben; die Finanzen fragen dessen Vorprüfung im
// Rechnungslauf und zeigen den Stand am Beleg (dependsOn 'einvoice' – einvoice hängt nicht an den Finanzen).
const GROUP = 'Finanzen'

export default defineModule({
  id: 'finance',
  title: 'Finanzen',
  description: 'Ausgangsrechnungen, Rechnungskorrekturen, offene Posten, Zahlungen, Mahnwesen, Periodenabschluss, Buchungsjournal und Export im DATEV-Format.',
  dependsOn: ['platform', 'masterdata', 'sales', 'purchasing', 'einvoice'],
  permissions: [
    definePermission({ key: 'finance.invoice.read', label: 'Rechnungen und Rechnungskorrekturen lesen', group: GROUP }),
    definePermission({
      key: 'finance.invoice.write',
      label: 'Rechnungen schreiben, korrigieren und stornieren',
      group: GROUP,
      description: 'Rechnungslauf über zugestellte Lieferungen, Rechnungskorrekturen aus Rücknahmen, Storno mit Gegenbeleg, Rechnung berichtigen (ersetzt eine Rechnung, auch nach Zahlung).',
    }),
    definePermission({
      key: 'finance.supplierInvoice.read',
      label: 'Zahlbare Eingangsrechnungen lesen',
      group: GROUP,
      description: 'Freigegebene Eingangsrechnungen mit Fälligkeit und Skontofrist. Erfasst und geprüft werden sie im Einkauf.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.supplierInvoice.write',
      label: 'Zahlungen an Lieferanten vorbereiten und als bezahlt buchen',
      group: GROUP,
      description: 'Zahlungsvorschlag, Zahlungsliste als Datei, Buchen der Auszahlung. Überwiesen wird im Online-Banking; der Kontoauszug lässt sich im Bankabgleich zuordnen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.openItem.read',
      label: 'Offene Posten und Kontoauszüge lesen',
      group: GROUP,
      description: 'Forderungen und Verbindlichkeiten mit Fälligkeit, Altersstruktur, Mahnstufe und Skontofrist.',
    }),
    definePermission({ key: 'finance.payment.read', label: 'Zahlungen lesen', group: GROUP }),
    definePermission({
      key: 'finance.payment.record',
      label: 'Zahlungen buchen, zuordnen und stornieren',
      group: GROUP,
      description: 'Zahlungseingang mit Zuordnung und Skonto, Guthaben verrechnen, Kleindifferenzen ausbuchen, Zahlung stornieren.',
    }),
    definePermission({
      key: 'finance.driverCash.settle',
      label: 'Fahrerkasse abrechnen',
      group: GROUP,
      description: 'Was an der Tür kassiert wurde, am Ende der Tour abgeben: Soll gegen abgegebenes Bargeld, Differenz mit Grund. Der Fahrer hält das Recht an seinem Fahrzeug.',
    }),
    definePermission({
      key: 'finance.driverCash.approve',
      label: 'Fahrerabrechnungen freigeben',
      group: GROUP,
      description: 'Vier-Augen-Regel: Die Freigabe durch eine zweite Person bucht die kassierten Beträge als Zahlungen auf die Kundenkonten.',
    }),
    definePermission({
      key: 'finance.invoice.send',
      label: 'Rechnungen und Gutschriften per E-Mail versenden',
      group: GROUP,
      description: 'Der Beleg steht im Mailtext, die E-Rechnung hängt an. Auch der Sammelversand nach dem Rechnungslauf.',
    }),
    definePermission({ key: 'finance.dunning.read', label: 'Mahnungen und Mahnverlauf lesen', group: GROUP, locationScoped: false }),
    definePermission({
      key: 'finance.dunning.send',
      label: 'Mahnungen per E-Mail versenden',
      group: GROUP,
      description: 'Einzelne Mahnungen und ganze Mahnläufe an die hinterlegten Adressen der Kunden.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.dunning.run',
      label: 'Mahnlauf prüfen und bestätigen, Mahnsperren setzen',
      group: GROUP,
      description: 'Der Mahnvorschlag wird Zeile für Zeile geprüft und in einem Schritt bestätigt. Nichts wird automatisch gemahnt.',
      locationScoped: false,
    }),
    definePermission({ key: 'finance.dunning.settings', label: 'Mahnstufen, Gebühren und Texte einstellen', group: GROUP, locationScoped: false }),
    definePermission({
      key: 'finance.export.read',
      label: 'Buchungsjournal und Exporte lesen',
      group: GROUP,
      description: 'Jede Erlös-, Steuer-, Forderungs- und Verbindlichkeitsbuchung, abgeleitet aus den Belegen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.export.run',
      label: 'Buchungsstapel für die Steuerkanzlei erzeugen',
      group: GROUP,
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.export.mapping',
      label: 'Kontenzuordnung und Angaben für den Export pflegen',
      group: GROUP,
      locationScoped: false,
    }),
    // Bankabgleich (docs/bank-datev/SPEZIFIKATION.md §8): Bestätigen bucht über „Zahlungen buchen“ bzw. „Eingangsrechnungen
    // als bezahlt buchen“ – diese Rechte braucht es ZUSÄTZLICH; Buchungen auf Sachkonten haben ein eigenes Recht mit Grenze.
    definePermission({
      key: 'finance.bank.read',
      label: 'Bankkonten, Kontoauszüge und Bankabgleich lesen',
      group: GROUP,
      description: 'Eingelesene Kontoauszüge, jeden Umsatz mit Zuordnungsvorschlag und den Stand des Abgleichs.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.bank.import',
      label: 'Kontoauszüge einlesen',
      group: GROUP,
      description: 'Kontoauszüge im Format camt.053 hochladen. Schon eingelesene Auszüge und Umsätze werden erkannt und nicht doppelt übernommen.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.bank.match',
      label: 'Bankumsätze zuordnen und Zuordnungen stornieren',
      group: GROUP,
      description: 'Vorschläge bestätigen – einzeln oder gesammelt. Gebucht wird über „Zahlungen buchen“ bzw. „Eingangsrechnungen als bezahlt buchen“; diese Rechte braucht es zusätzlich.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.bank.book',
      label: 'Bankumsätze auf Sachkonten buchen',
      group: GROUP,
      description: 'Umsätze ohne offenen Posten (Gebühren, Miete, Geldtransit) auf ein Sachkonto buchen und solche Buchungen stornieren. Über der Grenze bestätigt eine zweite Person mit ihrer PIN.',
      locationScoped: false,
      limits: [{ key: 'maxBookingCents', label: 'Höchster Betrag je Buchung auf ein Sachkonto', unit: 'cents' }],
    }),
    definePermission({
      key: 'finance.bank.accounts',
      label: 'Bankkonten anlegen und ändern',
      group: GROUP,
      description: 'IBAN, BIC, Bezeichnung und Sachkonto der Bankkonten des Betriebs.',
      locationScoped: false,
    }),
    // Belege nach der Zustellung (docs/belege-nach-zustellung/SPEZIFIKATION.md): Schalter des Betriebs, alle standardmäßig aus
    definePermission({
      key: 'finance.automation.manage',
      label: 'Rechnungsautomatik einstellen',
      group: GROUP,
      description: 'Rechnung nach der Zustellung und fällige Sammelrechnungen automatisch erstellen, automatisch erstellte Rechnungen per E-Mail senden.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.period.close',
      label: 'Monat abschließen',
      group: GROUP,
      description: 'Sperrt den Monat für Belege und Zahlungen – nach der Prüfliste.',
      locationScoped: false,
    }),
    definePermission({
      key: 'finance.period.reopen',
      label: 'Abgeschlossenen Monat wieder öffnen',
      group: GROUP,
      description: 'Starkes Recht: hebt den Abschluss auf. Nur mit Begründung, sichtbar im Verlauf.',
      locationScoped: false,
    }),
  ],
})
